import * as React from 'react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { RateSeniorityLevelDto } from '@/api/rate-collection-client';
import { seniorityLabel } from '@/lib/rate-collection-labels';
import type {
  CellEditState,
  CellSaveStatus,
} from '@/routes/supplier/rate-collection/use-saisie-autosave';

// =============================================================================
// tjm-cell-editor.tsx — Cellule éditable TJM (mono / tabular-nums) de la grille.
// =============================================================================
// Saisie d'un entier > 0 (euros). Validation INLINE (UX) DOUBLÉE serveur (autorité API).
// a11y WCAG 2.1 AA : `aria-label` explicite (profil + niveau), `aria-invalid` sur erreur,
// `aria-describedby` vers le statut (lecteur d'écran), `inputMode=numeric`. La COULEUR de
// statut est TOUJOURS doublée d'un libellé texte (jamais le seul porteur d'information).
// Navigation clavier native (input + bouton retry focusables).
//
// PERF (ENF-01 / ST-NFR-01) : la frappe ne déclenche AUCUN appel réseau (autosave sur blur /
// intervalle). L'édition perçue reste < 150 ms (état local, pas de refetch de grille).

/** Phrase d'état lisible par lecteur d'écran (renfort, jamais la couleur seule). */
function statusText(status: CellSaveStatus): string {
  switch (status) {
    case 'saving':
      return 'Enregistrement en cours';
    case 'saved':
      return 'Enregistré';
    case 'error':
      return 'Échec de l’enregistrement';
    case 'invalid':
      return 'Valeur invalide';
    case 'dirty':
      return 'Modification non enregistrée';
    case 'idle':
    default:
      return '';
  }
}

/** Classe de renfort visuel du statut (token sémantique ; doublée d'un texte). */
function statusClass(status: CellSaveStatus): string {
  switch (status) {
    case 'error':
    case 'invalid':
      return 'text-destructive';
    case 'saving':
      return 'text-muted-foreground';
    case 'saved':
      return 'text-foreground';
    default:
      return 'text-muted-foreground';
  }
}

export interface TjmCellEditorProps {
  readonly profileCode: string;
  readonly profileDisplayLabel: string;
  readonly seniorityLevel: RateSeniorityLevelDto;
  readonly state: CellEditState;
  readonly disabled: boolean;
  readonly onDraftChange: (draft: string) => void;
  readonly onBlur: () => void;
  readonly onRetry: () => void;
  /**
   * A0-9 / S35-CIBLAGE-COUPLE (DBU §3.5 l.2810) : couple (profil, niveau) NON concerné par la campagne ⇒
   * cellule GRISÉE NON SAISISSABLE, doublée d'un libellé texte « Non concerné » (WCAG : jamais la couleur
   * seule) et d'un `aria-label` explicite. `false` par défaut. La cellule reste affichée (verticalité).
   */
  readonly notTargeted?: boolean;
  /**
   * S13 (G0-S13-2) : cellule verrouillée par un AUTRE collaborateur (EditLock concurrent) ⇒
   * lecture seule SIGNALÉE (badge « cellule éditée par … » + tooltip, OPP-04). `false` par défaut.
   */
  readonly lockedByOther?: boolean;
  /** S13 : détenteur concurrent du verrou (libellé du badge). */
  readonly holderContactId?: string | null;
  /** S13 : focus de la cellule (déclenche l'acquisition du verrou EditLock). */
  readonly onFocus?: () => void;
  /**
   * S19 (D1-D, WCAG 2.1.1) : coordonnées 0-based de la cellule dans la grille SAISIE, émises
   * comme `data-grid-row` / `data-grid-col` sur l'input. Consommées par le handler de navigation
   * clavier inter-cellules (flèches) délégué au `<table>`. `undefined` ⇒ pas de nav grille.
   */
  readonly rowIndex?: number;
  readonly colIndex?: number;
  /**
   * Maquette #85 (point 10) : adornement « € » DÉCORATIF (aria-hidden) collé à la valeur (mono). Le
   * montant reste « en euros » via l'aria-label (le symbole ne porte aucune information a11y). `false`
   * par défaut (rendu hors grille / cellule non concernée).
   */
  readonly showEuroAdornment?: boolean;
  /**
   * Maquette #85 (point 14) : placeholder de la cellule VIDE (« — »). Décoratif ; l'état « à saisir »
   * est porté par la mention texte ambre rendue par le parent. `undefined` ⇒ pas de placeholder.
   */
  readonly placeholder?: string;
}

export function TjmCellEditor({
  profileCode,
  profileDisplayLabel,
  seniorityLevel,
  state,
  disabled,
  onDraftChange,
  onBlur,
  onRetry,
  notTargeted = false,
  lockedByOther = false,
  holderContactId = null,
  onFocus,
  rowIndex,
  colIndex,
  showEuroAdornment = false,
  placeholder,
}: TjmCellEditorProps): React.JSX.Element {
  const baseId = `tjm-${profileCode}-${seniorityLevel}`.replace(/[^a-zA-Z0-9_-]/g, '_');
  const statusId = `${baseId}-status`;
  const lockId = `${baseId}-lock`;
  const notTargetedId = `${baseId}-untargeted`;
  const invalid = state.status === 'invalid' || state.status === 'error';
  // A0-9 : un couple non concerné enrichit l'aria-label (le lecteur d'écran annonce le motif du grisage).
  const label = notTargeted
    ? `TJM ${profileDisplayLabel}, niveau ${seniorityLabel(seniorityLevel)} : couple non concerné par cette campagne, non saisissable`
    : `TJM ${profileDisplayLabel}, niveau ${seniorityLabel(seniorityLevel)}, en euros`;
  const text = statusText(state.status);
  // A0-9 : un couple NON concerné est non saisissable (grisé), au même titre qu'un verrou global.
  const effectiveDisabled = disabled || notTargeted;
  const holderName = holderContactId ?? 'un autre collaborateur';
  const lockMessage = `Cellule en cours d'édition par ${holderName}`;
  const describedBy =
    [
      notTargeted ? notTargetedId : undefined,
      lockedByOther ? lockId : undefined,
      text !== '' ? statusId : undefined,
    ]
      .filter((v): v is string => v !== undefined)
      .join(' ') || undefined;

  // S19 (D1-D) : coordonnées de cellule pour la navigation clavier inter-cellules (flèches).
  // Émises uniquement si les DEUX indices sont fournis (rendu hors grille = pas d'attribut).
  const gridCoords =
    rowIndex !== undefined && colIndex !== undefined
      ? { 'data-grid-row': rowIndex, 'data-grid-col': colIndex }
      : {};

  return (
    <div className="flex flex-col gap-1">
      <div className="relative">
        <Input
          id={baseId}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          aria-label={label}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          {...gridCoords}
          {...(placeholder !== undefined ? { placeholder } : {})}
          // Verrou concurrent : champ en lecture seule (la valeur reste lisible, non éditable).
          readOnly={lockedByOther && !effectiveDisabled}
          disabled={effectiveDisabled}
          value={state.draft}
          onChange={(e) => onDraftChange(e.target.value)}
          onFocus={() => onFocus?.()}
          onBlur={onBlur}
          // mono + tabular-nums : alignement des chiffres (lisibilité grille de montants). Le padding
          // droit réserve la place de l'adornement « € » quand il est rendu (maquette #85 point 10).
          // Maquette #90 : cellule PROPRE — pas de « box » d'input (bordure/fond retirés) ; la valeur se
          // lit comme du texte aligné à droite. La bordure + le fond réapparaissent au focus (affordance
          // d'édition) ; en lecture seule (grille soumise) la cellule reste plate, sans grisage.
          className={cn(
            'h-9 border-transparent bg-transparent px-1 text-right font-mono text-[15px] tabular-nums shadow-none focus-visible:border-input focus-visible:bg-background',
            showEuroAdornment && 'pr-6',
          )}
        />
        {showEuroAdornment && (
          // « € » DÉCORATIF (l'aria-label porte déjà « en euros ») : jamais le seul porteur d'info.
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-2 flex items-center font-mono text-xs text-muted-foreground"
          >
            €
          </span>
        )}
      </div>
      {notTargeted && (
        // A0-9 (DBU l.2810) : couple non concerné — libellé texte explicite (jamais la couleur/grisage
        // seuls porteurs de l'information, WCAG 2.1 AA). Doublé de l'aria-label + aria-describedby.
        <span
          id={notTargetedId}
          className="text-xs font-medium text-muted-foreground"
        >
          Non concerné
        </span>
      )}
      {lockedByOther && (
        // Badge OPP-04 : la couleur n'est JAMAIS le seul porteur — un LIBELLÉ texte + une icône
        // verrou (titre = tooltip) portent l'information ; `title` fournit l'infobulle native.
        <span
          id={lockId}
          className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-xs font-medium text-foreground"
          title={lockMessage}
        >
          <span aria-hidden="true">🔒</span>
          <span>Éditée par {holderName}</span>
        </span>
      )}
      <div className="flex min-h-[1.25rem] items-center gap-2">
        {text !== '' && (
          <span
            id={statusId}
            className={cn('text-xs', statusClass(state.status))}
            // Annonce non intrusive des bascules de statut au lecteur d'écran.
            aria-live="polite"
          >
            {text}
          </span>
        )}
        {state.status === 'error' && (
          <button
            type="button"
            onClick={onRetry}
            className="text-xs font-medium text-destructive underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          >
            Réessayer
          </button>
        )}
        {state.error !== null && state.status === 'invalid' && (
          <span className="sr-only">{state.error}</span>
        )}
      </div>
    </div>
  );
}
